'use client';

import { mergeRefs } from '@react-aria/utils';
import clsx from 'clsx';
import {
  type ForwardedRef,
  type ReactNode,
  type UIEventHandler,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useFilter, useId, useInteractOutside, usePreventScroll } from 'react-aria';
import {
  Autocomplete as RACAutocomplete,
  Button as RACButton,
  Collection as RACCollection,
  Header as RACHeader,
  LabelContext as RACLabelContext,
  ListBox as RACListBox,
  ListBoxItem as RACListBoxItem,
  type ListBoxItemProps as RACListBoxItemProps,
  ListBoxLoadMoreItem as RACListBoxLoadMoreItem,
  type ListBoxProps as RACListBoxProps,
  ListBoxSection as RACListBoxSection,
  ListLayout as RACListLayout,
  type ListLayoutOptions as RACListLayoutOptions,
  Select as RACSelect,
  type SelectProps as RACSelectProps,
  SelectStateContext as RACSelectStateContext,
  SelectValue as RACSelectValue,
  Text as RACText,
  Virtualizer as RACVirtualizer,
  useSlottedContext,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { IconCheck, IconChevronDown } from '../../icons/index.js';
import { isProduction } from '../../utils/is-production.js';
import { stringLikeChildrenToString } from '../../utils/string-like-children-to-string.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { CollapsibleList } from '../internal/collapsible-list/collapsible-list.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import { XButton } from '../internal/x-button/x-button.js';
import { Spinner } from '../spinner/spinner.js';
import { TagGroup } from '../tag/tag-group.js';
import { Tag } from '../tag/tag.js';
import { SearchField } from '../text-inputs/search-field.js';
import type {
  ApiProps,
  AsyncItemLoadingProps,
  CollectionItem,
  CollectionProps,
  CommonProps,
  FieldProps,
  Key,
  StringLikeChildren,
} from '../types.js';
import { Text } from '../typography/text.js';
import { textStyle } from '../typography/utils.js';
import { UNSTABLE_VisuallyHidden } from '../visually-hidden/visually-hidden.js';

export type UNSTABLE_SelectSelectionMode = 'single' | 'multiple';

const VIRTUAL_LAYOUT_OPTIONS: RACListLayoutOptions = {
  estimatedRowHeight: 40,
  estimatedHeadingHeight: 32,
  loaderHeight: 40,
  padding: 0,
  gap: 0,
};

export interface SelectProps<
  T extends CollectionItem = CollectionItem,
  M extends UNSTABLE_SelectSelectionMode = 'single',
>
  extends
    CommonProps,
    FieldProps<M extends 'single' ? Key : Key[]>,
    CollectionProps<T>,
    AsyncItemLoadingProps,
    ApiProps<SelectApi>,
    Pick<
      RACSelectProps<T, M>,
      | 'isDisabled'
      | 'isRequired'
      | 'isInvalid'
      | 'autoComplete'
      | 'autoFocus'
      | 'placeholder'
      | 'onFocus'
      | 'onBlur'
      | 'onOpenChange'
    > {
  /** The currently selected key in the collection (controlled). */
  selectedKey?: Key | null;
  /** The initial selected key in the collection (uncontrolled). */
  defaultSelectedKey?: Key;
  /** Handler that is called when the selection changes. */
  onSelectionChange?: (key: Key | null) => void;
  /**
   * Whether the list of options should be virtualized. Use this as a performance optimization for large lists.
   * @default false
   */
  isVirtualized?: boolean;
  /** Handler that is called when the list of items is scrolled. */
  onScroll?: UIEventHandler<HTMLDivElement>;
  /**
   * The selection mode of the select.
   * @default 'single'
   */
  UNSTABLE_selectionMode?: M;
  /** The value of the select (controlled). */
  UNSTABLE_value?: M extends 'single' ? Key | null : Key[];
  /** The initial value of the select (uncontrolled). */
  UNSTABLE_defaultValue?: M extends 'single' ? Key : Key[];
  /** Handler that is called when the value changes. */
  UNSTABLE_onChange?: (key: M extends 'single' ? Key | null : Key[]) => void;
  /**
   * How the selected value is displayed.
   * @default 'text'
   */
  UNSTABLE_valueDisplay?: 'text' | 'tags';
  /**
   * Whether the list of options should be filterable.
   * @default false
   */
  UNSTABLE_isFilterable?: boolean;
  /** The value of the filter input (controlled). */
  UNSTABLE_filterValue?: string;
  /** The initial value of the filter input (uncontrolled). */
  UNSTABLE_defaultFilterValue?: string;
  /** Handler that is called when the filter value changes. */
  UNSTABLE_onFilterChange?: (value: string) => void;
  /** A custom filter function to use instead of the default filter. */
  UNSTABLE_filter?: ((textValue: string, inputValue: string, node: { key: Key; value: T | null }) => boolean) | null;
  UNSTABLE_isClearable?: boolean;
}

/** The imperative API exposed by the `Select` component. */
export interface SelectApi {
  /** Focuses the trigger button and opens the list of items. */
  focus: () => void;
}

function Select<T extends CollectionItem, M extends UNSTABLE_SelectSelectionMode = 'single'>(
  {
    children,
    items,
    label,
    description,
    error,
    UNSAFE_className,
    UNSAFE_style,
    onScroll,
    isVirtualized = false,
    apiRef,
    UNSTABLE_isLoading: isLoading,
    UNSTABLE_onLoadMore: onLoadMore,
    selectedKey,
    defaultSelectedKey,
    onSelectionChange,
    UNSTABLE_selectionMode,
    UNSTABLE_value,
    UNSTABLE_defaultValue,
    UNSTABLE_onChange,
    UNSTABLE_valueDisplay: valueDisplay = 'text',
    UNSTABLE_isFilterable: isFilterable = false,
    UNSTABLE_filterValue: filterValue,
    UNSTABLE_defaultFilterValue: defaultFilterValue,
    UNSTABLE_onFilterChange: onFilterChange,
    UNSTABLE_filter: filter,
    UNSTABLE_isClearable: isClearable = false,
    ...props
  }: SelectProps<T, M>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('Select requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  if (!isProduction() && items != null && typeof children !== 'function') {
    console.warn('`Select` requires `children` to be a function when using `items` prop');
  }

  useImperativeHandle(
    apiRef,
    () => ({
      focus: () => triggerRef.current?.focus(),
    }),
    [],
  );

  const listBox = (
    <SelectListBox items={items} isLoading={isLoading} onLoadMore={onLoadMore} onScroll={onScroll}>
      {children}
    </SelectListBox>
  );

  const listBoxWithVirtualizer = isVirtualized ? (
    <RACVirtualizer layout={RACListLayout} layoutOptions={VIRTUAL_LAYOUT_OPTIONS}>
      {listBox}
    </RACVirtualizer>
  ) : (
    listBox
  );

  const listBoxWithFilter = isFilterable ? (
    <SelectFilter
      filterValue={filterValue}
      defaultFilterValue={defaultFilterValue}
      onFilterChange={onFilterChange}
      filter={filter}
    >
      {listBoxWithVirtualizer}
    </SelectFilter>
  ) : (
    listBoxWithVirtualizer
  );

  return (
    <RACSelect
      {...props}
      ref={ref}
      className={clsx('cim-select', UNSAFE_className)}
      style={UNSAFE_style}
      selectedKey={selectedKey}
      defaultSelectedKey={defaultSelectedKey}
      onSelectionChange={onSelectionChange}
      value={UNSTABLE_value}
      defaultValue={UNSTABLE_defaultValue}
      onChange={UNSTABLE_onChange}
      selectionMode={UNSTABLE_selectionMode}
      allowsEmptyCollection={isLoading}
    >
      <FormFieldLabel isRequired={props.isRequired} isDisabled={props.isDisabled}>
        {label}
      </FormFieldLabel>
      <SelectButton ref={triggerRef} valueDisplay={valueDisplay} isClearable={isClearable} />
      <FormFieldError>{error}</FormFieldError>
      <FormFieldDescription>{description}</FormFieldDescription>
      <SelectPopover>{listBoxWithFilter}</SelectPopover>
      {UNSTABLE_selectionMode === 'multiple' && valueDisplay === 'tags' && <SelectTags />}
    </RACSelect>
  );
}

interface SelectPopoverProps {
  children: ReactNode;
}

function SelectPopover({ children }: SelectPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const selectState = useContext(RACSelectStateContext);

  // Popover is non-modal, but we still want to close it when the user interacts outside
  useInteractOutside({
    ref: popoverRef,
    onInteractOutside: () => {
      selectState?.close();
    },
    isDisabled: !selectState?.isOpen,
  });

  // Popover is non-modal, but we still want to prevent scrolling when it is open
  usePreventScroll({ isDisabled: !selectState?.isOpen });

  return (
    <CollapsibleList
      ref={popoverRef}
      offset={5} // 1px border + 4px actual offset
      maxHeight={315}
      // Set to non-modal so that interactions like clicking the clear button can be performed without first having to close the popover
      isNonModal
    >
      {children}
    </CollapsibleList>
  );
}

interface SelectButtonProps {
  valueDisplay: SelectProps['UNSTABLE_valueDisplay'];
  isClearable?: SelectProps['UNSTABLE_isClearable'];
}

const SelectButton = forwardRef<SelectButtonProps, HTMLButtonElement>(({ valueDisplay, isClearable }, ref) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const collectionMessages = useLocalizedMessages('UNSTABLE_collection');

  return (
    <div className="cim-select-button">
      <RACButton ref={mergeRefs(ref, triggerRef)} className="cim-select-button-trigger" />
      <RACSelectValue className={clsx('cim-select-button-value', textStyle({ variant: 'body', alignment: 'start' }))}>
        {({ selectedText, defaultChildren, isPlaceholder, state }) => {
          if (valueDisplay === 'tags' && state.selectedItems.length > 0) {
            return (
              <Text as="span" variant="body" tone="subtle">
                {collectionMessages.format('itemsSelected', { selectedItemCount: state.selectedItems.length })}
              </Text>
            );
          }

          return isPlaceholder ? defaultChildren : selectedText;
        }}
      </RACSelectValue>
      {isClearable && (
        <SelectClearButton
          // Move focus to trigger button, because button will disappear after clearing
          onClear={() => triggerRef.current?.focus()}
        />
      )}
      <IconChevronDown UNSAFE_className="cim-select-button-chevron" />
    </div>
  );
});

function SelectClearButton({ onClear }: { onClear?: () => void }) {
  const selectState = useContext(RACSelectStateContext);

  if (!selectState || selectState.selectedItems.length === 0) {
    return null;
  }

  return (
    <XButton
      // TODO: i18n once stabilized
      aria-label="Clear selection"
      iconType="circled"
      onPress={() => {
        selectState.setValue(null);
        onClear?.();
      }}
      slot={null}
      UNSAFE_className="cim-select-button-clear"
    />
  );
}

function SelectListBox<T extends CollectionItem>({
  children,
  items,
  isLoading,
  onLoadMore,
  ...props
}: RACListBoxProps<T> & {
  isLoading?: boolean;
  onLoadMore?: () => void;
}) {
  const collectionMessages = useLocalizedMessages('UNSTABLE_collection');
  const selectState = useContext(RACSelectStateContext);
  const isEmpty = selectState?.collection.size === 0;

  return (
    <RACListBox
      className="cim-select-listbox"
      renderEmptyState={() =>
        !isLoading && (
          // TODO: i18n once stabilized
          <Text
            as="p"
            variant="body"
            tone="subtle"
            marginX={8}
            marginY={8}
            UNSAFE_className="cim-select-listbox-empty-state"
          >
            No results found.
          </Text>
        )
      }
      {...props}
    >
      <RACCollection items={items}>{children}</RACCollection>
      <RACListBoxLoadMoreItem onLoadMore={onLoadMore} isLoading={isLoading}>
        <Spinner aria-label={collectionMessages.format(isEmpty ? 'loading' : 'loadingMore')} size="small" marginY={8} />
      </RACListBoxLoadMoreItem>
    </RACListBox>
  );
}

function SelectFilter<T extends CollectionItem>({
  children,
  filterValue,
  defaultFilterValue,
  onFilterChange,
  filter,
}: RACListBoxProps<T> & {
  children: ReactNode;
  filterValue?: string;
  defaultFilterValue?: string;
  onFilterChange?: (value: string) => void;
  filter?: ((textValue: string, inputValue: string, node: { key: Key; value: T | null }) => boolean) | null;
}) {
  const { contains } = useFilter({ sensitivity: 'base' });
  const filterFn = useMemo(() => {
    if (filter === null) return undefined;
    if (filter == null) return contains;

    return (textValue: string, inputValue: string, node: { key: Key; value: T | null }) => {
      return filter(textValue, inputValue, { key: node.key, value: node.value });
    };
  }, [filter, contains]);

  return (
    <RACAutocomplete<T>
      filter={filterFn}
      inputValue={filterValue}
      defaultInputValue={defaultFilterValue}
      onInputChange={onFilterChange}
    >
      <SearchField
        // TODO: i18n once stabilized
        aria-label="Search"
        placeholder="Search"
        autoFocus
        marginX={8}
        marginTop={8}
      />
      {children}
    </RACAutocomplete>
  );
}

function SelectTags() {
  const labelContext = useSlottedContext(RACLabelContext);
  const selectedItemsLabelId = useId();

  return (
    <RACSelectValue>
      {({ state }) => {
        if (state.selectedItems.length === 0) {
          return <></>;
        }

        return (
          <>
            <TagGroup
              aria-labelledby={[labelContext?.id, selectedItemsLabelId].filter(Boolean).join(' ')}
              items={state.selectedItems
                .filter((item) => item != null)
                .map((item) => ({ id: item.key, label: item.textValue }))}
              onRemove={(keys) => {
                if (Array.isArray(state.value)) {
                  state.setValue(state.value.filter((k) => !keys.has(k)));
                }
              }}
              marginTop={8}
            >
              {(item) => <Tag>{item.label}</Tag>}
            </TagGroup>
            {/* TODO: i18n once stabilized */}
            <UNSTABLE_VisuallyHidden id={selectedItemsLabelId}>selected items</UNSTABLE_VisuallyHidden>
          </>
        );
      }}
    </RACSelectValue>
  );
}

/**
 * Allows users to select one item from a collapsible list.
 *
 * See [select usage guidelines](https://ui.cimpress.io/components/select/).
 */
const _Select = withStyleProps(forwardRef(Select), 'Select');

export { _Select as Select };

export interface SelectItemProps extends Pick<RACListBoxItemProps, 'isDisabled' | 'onHoverStart' | 'onHoverEnd'> {
  /** The ID of the item. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the label. */
  children: StringLikeChildren;
}

/** Renders a single list item within `Select`. */
export function SelectItem({ children, ...props }: SelectItemProps) {
  const textValue = stringLikeChildrenToString(children);

  return (
    <RACListBoxItem {...props} className="cim-select-item" textValue={textValue}>
      {({ isDisabled }) => (
        <>
          <IconCheck />

          <RACText slot="label" className={textStyle({ variant: 'body', tone: isDisabled ? 'muted' : 'base' })}>
            {children}
          </RACText>
        </>
      )}
    </RACListBoxItem>
  );
}

SelectItem.displayName = 'SelectItem';

export interface SelectSectionProps<T extends CollectionItem> extends CollectionProps<T> {
  /** The ID of the section. Has to be unique across all sections and items. */
  id?: Key;
  /** The content to display as the section title. */
  title: string;
}

/** Groups list items within `Select` into a section. */
export function SelectSection<T extends CollectionItem>({ title, children, items, ...props }: SelectSectionProps<T>) {
  if (!isProduction() && items != null && typeof children !== 'function') {
    console.warn('`SelectSection` requires `children` to be a function when using `items` prop');
  }

  return (
    <RACListBoxSection {...props} className="cim-select-section">
      <RACHeader
        className={clsx('cim-select-section-header', textStyle({ variant: 'small-semibold', tone: 'subtle' }))}
      >
        {title}
      </RACHeader>
      <RACCollection items={items}>{children}</RACCollection>
    </RACListBoxSection>
  );
}

SelectSection.displayName = 'SelectSection';
